'use client';

import React, { useState, useMemo } from 'react';
import { Search, Filter, Plus, FileText } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Link from 'next/link';
import { toast } from 'sonner';

export type SO = {
  id: string;
  soNumber: string;
  jobId: string;
  jobName: string;
  linkedPo: string;
  client: string;
  createdDate: string;
  total: string;
  totalRaw: number;
  status: string;
  invoiceStatus: string;
  invoiceNumber: string;
};

export const soData: SO[] = [
  { id: 'so-3244', soNumber: 'SO-3244', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', linkedPo: 'PO-2854', client: 'TechConf Events Ltd', createdDate: '16/08/2026', total: '£13,200', totalRaw: 13200, status: 'draft', invoiceStatus: '—', invoiceNumber: '—' },
  { id: 'so-3243', soNumber: 'SO-3243', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', linkedPo: 'PO-2853', client: 'TechConf Events Ltd', createdDate: '15/08/2026', total: '£8,160', totalRaw: 8160, status: 'fulfilled', invoiceStatus: 'overdue', invoiceNumber: 'INV-10482' },
  { id: 'so-3242', soNumber: 'SO-3242', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', linkedPo: 'PO-2852', client: 'TechConf Events Ltd', createdDate: '15/08/2026', total: '£9,840', totalRaw: 9840, status: 'confirmed', invoiceStatus: '—', invoiceNumber: '—' },
  { id: 'so-3241', soNumber: 'SO-3241', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', linkedPo: 'PO-2851', client: 'TechConf Events Ltd', createdDate: '15/08/2026', total: '£14,880', totalRaw: 14880, status: 'confirmed', invoiceStatus: '—', invoiceNumber: '—' },
  { id: 'so-3239', soNumber: 'SO-3239', jobId: 'JOB-CRM-9921', jobName: 'Branded hoodies — RetailCo Q3', linkedPo: 'PO-2848', client: 'RetailCo UK', createdDate: '13/08/2026', total: '£11,520', totalRaw: 11520, status: 'confirmed', invoiceStatus: 'overdue', invoiceNumber: 'INV-10477' },
  { id: 'so-3238', soNumber: 'SO-3238', jobId: 'JOB-CRM-9921', jobName: 'Branded hoodies — RetailCo Q3', linkedPo: 'PO-2847', client: 'RetailCo UK', createdDate: '13/08/2026', total: '£15,840', totalRaw: 15840, status: 'invoiced', invoiceStatus: 'due', invoiceNumber: 'INV-10481' },
  { id: 'so-3232', soNumber: 'SO-3232', jobId: 'JOB-CRM-9919', jobName: 'Promo kit — SaaS Summit', linkedPo: 'PO-2842', client: 'SaaS Summit Ltd', createdDate: '11/08/2026', total: '£8,640', totalRaw: 8640, status: 'confirmed', invoiceStatus: '—', invoiceNumber: '—' },
  { id: 'so-3231', soNumber: 'SO-3231', jobId: 'JOB-CRM-9919', jobName: 'Promo kit — SaaS Summit', linkedPo: 'PO-2843', client: 'SaaS Summit Ltd', createdDate: '11/08/2026', total: '£11,280', totalRaw: 11280, status: 'confirmed', invoiceStatus: 'sent', invoiceNumber: 'INV-10473' },
  { id: 'so-3225', soNumber: 'SO-3225', jobId: 'JOB-CRM-9917', jobName: 'Tote bags — GreenFest', linkedPo: 'PO-2838', client: 'GreenFest CIC', createdDate: '08/08/2026', total: '£5,760', totalRaw: 5760, status: 'fulfilled', invoiceStatus: 'paid', invoiceNumber: 'INV-10480' },
  { id: 'so-3218', soNumber: 'SO-3218', jobId: 'JOB-CRM-9915', jobName: 'Embroidered polos — HealthOrg', linkedPo: 'PO-2835', client: 'HealthOrg NHS', createdDate: '05/08/2026', total: '£37,200', totalRaw: 37200, status: 'invoiced', invoiceStatus: 'sent', invoiceNumber: 'INV-10478' },
  { id: 'so-3211', soNumber: 'SO-3211', jobId: 'JOB-CRM-9912', jobName: 'Caps and beanies — SportsBrand', linkedPo: 'PO-2831', client: 'SportsBrand Ltd', createdDate: '01/08/2026', total: '£21,840', totalRaw: 21840, status: 'invoiced', invoiceStatus: 'overdue', invoiceNumber: 'INV-10476' },
  { id: 'so-3201', soNumber: 'SO-3201', jobId: 'JOB-CRM-9908', jobName: 'Desk kits — FinanceCorp', linkedPo: 'PO-2821', client: 'FinanceCorp PLC', createdDate: '28/07/2026', total: '£11,760', totalRaw: 11760, status: 'fulfilled', invoiceStatus: 'reconciled', invoiceNumber: 'INV-10474' },
];

const statusOptions = ['All statuses', 'Draft', 'Confirmed', 'Fulfilled', 'Invoiced'];
const clientOptions = ['All clients', ...Array.from(new Set(soData.map(s => s.client)))];

export default function SOContent() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [clientFilter, setClientFilter] = useState('All clients');

  const filtered = useMemo(() => {
    return soData.filter(so => {
      const matchSearch = !search ||
        so.soNumber.toLowerCase().includes(search.toLowerCase()) ||
        so.jobName.toLowerCase().includes(search.toLowerCase()) ||
        so.client.toLowerCase().includes(search.toLowerCase()) ||
        so.linkedPo.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'All statuses' || so.status === statusFilter.toLowerCase();
      const matchClient = clientFilter === 'All clients' || so.client === clientFilter;
      return matchSearch && matchStatus && matchClient;
    });
  }, [search, statusFilter, clientFilter]);

  const totalValue = filtered.reduce((s, so) => s + so.totalRaw, 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--foreground)' }}>Sales orders</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--slate)' }}>
            {soData.length} total — one per purchase order — total value £{soData.reduce((s, so) => s + so.totalRaw, 0).toLocaleString('en-GB')}
          </p>
        </div>
        <button
          onClick={() => toast.info('Create SO — connect to POST /api/sales-orders')}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md text-white transition-all active:scale-95"
          style={{ backgroundColor: 'var(--accent)' }}
        >
          <Plus size={15} />
          Create SO
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total SO value (filtered)', value: `£${totalValue.toLocaleString('en-GB')}`, color: 'var(--foreground)' },
          { label: 'Confirmed', value: String(filtered.filter(s => s.status === 'confirmed').length), color: 'var(--accent)' },
          { label: 'Invoiced', value: String(filtered.filter(s => s.status === 'invoiced').length), color: '#3B5BDB' },
          { label: 'Overdue invoices', value: String(filtered.filter(s => s.invoiceStatus === 'overdue').length), color: '#B91C1C' },
        ].map(s => (
          <div
            key={`so-summary-${s.label}`}
            className="rounded-lg border px-4 py-3"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--slate)' }}>{s.label}</p>
            <p className="text-xl font-bold tabular-nums" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div
        className="flex flex-wrap items-center gap-3 p-4 rounded-xl border"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div
          className="flex items-center gap-2 flex-1 min-w-[200px] px-3 py-2 rounded-md border"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)' }}
        >
          <Search size={14} style={{ color: 'var(--slate)' }} />
          <input
            type="text"
            placeholder="Search SO number, PO, job, or client…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 text-sm outline-none bg-transparent"
            style={{ color: 'var(--foreground)' }}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--slate)' }} />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm rounded-md border outline-none appearance-none cursor-pointer"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
            >
              {statusOptions.map(s => <option key={`so-status-opt-${s}`}>{s}</option>)}
            </select>
          </div>
          <select
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-md border outline-none appearance-none cursor-pointer"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)', maxWidth: '200px' }}
          >
            {clientOptions.map(c => <option key={`so-client-opt-${c}`}>{c}</option>)}
          </select>
        </div>
        <span className="text-xs ml-auto" style={{ color: 'var(--slate)' }}>
          {filtered.length} of {soData.length} SOs
        </span>
      </div>

      {/* Table */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--cloud)' }}>
                {['SO number', 'Job', 'Linked PO', 'Client', 'Created', 'SO total', 'Status', 'Invoice status', ''].map((h, i) => (
                  <th
                    key={`so-th-${i}`}
                    className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                    style={{ color: 'var(--slate)', letterSpacing: '0.05em' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-16">
                    <FileText size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>No sales orders match your filters</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--slate)' }}>Sales orders are generated one-to-one against purchase orders</p>
                  </td>
                </tr>
              ) : (
                filtered.map(so => (
                  <tr
                    key={so.id}
                    className="row-hover border-b"
                    style={{
                      borderColor: 'var(--border)',
                      backgroundColor: so.invoiceStatus === 'overdue' ? 'var(--amber-light)' : undefined,
                    }}
                  >
                    <td className="px-4 py-3">
                      <Link href={`/sales-orders/${so.id}`} className="text-xs font-mono font-semibold hover:underline" style={{ color: 'var(--accent)' }}>
                        {so.soNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3 max-w-[180px]">
                      <p className="text-xs font-mono" style={{ color: 'var(--slate)' }}>{so.jobId}</p>
                      <p className="text-sm truncate font-medium" style={{ color: 'var(--foreground)' }}>{so.jobName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono" style={{ color: 'var(--accent)' }}>{so.linkedPo}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span style={{ color: 'var(--foreground)' }}>{so.client}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span style={{ color: 'var(--slate)' }}>{so.createdDate}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="tabular-nums font-semibold" style={{ color: 'var(--accent)' }}>{so.total}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={so.status as never} />
                    </td>
                    <td className="px-4 py-3">
                      {so.invoiceStatus === '—' ? (
                        <span className="text-xs" style={{ color: 'var(--slate)' }}>No invoice yet</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <StatusBadge status={so.invoiceStatus as never} pulse={so.invoiceStatus === 'overdue'} />
                          <span className="text-xs font-mono" style={{ color: 'var(--slate)' }}>{so.invoiceNumber}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        href={`/sales-orders/${so.id}`}
                        className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors hover:bg-muted whitespace-nowrap"
                        style={{ borderColor: 'var(--border)', color: 'var(--slate)' }}
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          className="flex items-center justify-between px-4 py-3 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          <p className="text-xs" style={{ color: 'var(--slate)' }}>
            Showing {filtered.length} of {soData.length} sales orders
          </p>
          <div className="flex items-center gap-1">
            {[1, 2].map(p => (
              <button
                key={`so-page-${p}`}
                className="w-7 h-7 text-xs rounded-md font-medium transition-colors"
                style={{
                  backgroundColor: p === 1 ? 'var(--ink)' : 'transparent',
                  color: p === 1 ? 'white' : 'var(--slate)',
                }}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--slate)' }}>Rows per page:</span>
            <select
              className="text-xs px-2 py-1 rounded-md border outline-none"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
            >
              {['15', '25', '50'].map(v => <option key={`so-rows-${v}`}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}